import { useMutation } from '@apollo/client';
import { useMemo } from 'react';
import { downloadFileUtil, getFileName } from '../../utils/file-download.util';
import { CreateRoleDocument, DeleteRoleDocument, DeleteRolesDocument, UpdateRoleDocument, useGetPrivilegeGroupsQuery as _useGetPrivilegeGroupsQuery, useGetRoleByIdLazyQuery as _useGetRoleByIdLazyQuery, useGetRolesLazyQuery as _useGetRolesLazyQuery, useGetRolesQuery as _useGetRolesQuery, useSearchRolesQuery as _useSearchRolesQuery, } from '../generated/graphql-types';
export const useGetRolesQuery = (input) => {
    const { data, error, loading, refetch } = _useGetRolesQuery({
        fetchPolicy: 'no-cache',
        variables: {
            data: input,
        },
    });
    return {
        error,
        loading,
        data: data?.getRoles,
        refetch,
    };
};
export const useGetRolesLazyQuery = (input) => {
    const [getRoles, { data, error, loading, refetch }] = _useGetRolesLazyQuery({
        fetchPolicy: 'no-cache',
        variables: {
            data: input,
        },
    });
    return {
        error,
        loading,
        data: data?.getRoles,
        refetch,
        getRoles,
    };
};
export const useSearchRolesQuery = (input) => {
    const { data, error, loading, refetch } = _useSearchRolesQuery({
        fetchPolicy: 'no-cache',
        variables: {
            data: input,
        },
    });
    const normalizedData = useMemo(() => {
        if (!data?.searchRoles)
            return undefined;
        const searchData = data.searchRoles.Data;
        return {
            Data: {
                roles: (searchData?.roles ?? []).map((role) => ({
                    id: role.id,
                    name: role.name,
                    description: role.description ?? '',
                    privileges: role.privileges ?? [],
                    author: role.author_id,
                    created: role.creation_time_in_millis,
                    modified: role.modification_time_in_millis,
                    isDeleted: role.deleted,
                    readOnly: role.permission === 'READ_ONLY',
                    groups: role.groups ?? [],
                })),
                isLastBatch: searchData?.isLastBatch ?? false,
            },
            Status: data.searchRoles.Status,
            Error: data.searchRoles.Error,
        };
    }, [data]);
    return {
        error,
        loading,
        data: normalizedData,
        refetch,
    };
};
export const useGetRoleByIdLazyQuery = (id) => {
    const [getRoleById, { data, error, loading }] = _useGetRoleByIdLazyQuery({
        variables: {
            id,
        },
        fetchPolicy: 'no-cache',
    });
    return {
        error,
        loading,
        data: data?.getRoleById,
        getRoleById,
    };
};
export const useDeleteRoleMutation = () => {
    const [_deleteRole, { data: result, error, loading }] = useMutation(DeleteRoleDocument);
    return {
        data: result?.Role_delete,
        error,
        loading,
        deleteRole: (id) => _deleteRole({
            variables: {
                id,
            },
        }),
    };
};
export const useDeleteRolesMutation = () => {
    const [_deleteRoles, { data: result, error, loading }] = useMutation(DeleteRolesDocument);
    return {
        data: result?.Roles_delete,
        error,
        loading,
        deleteRoles: (ids) => _deleteRoles({
            variables: {
                ids,
            },
        }),
    };
};
export const useGetPrivilegeGroupsQuery = () => {
    const { data, error, loading, refetch } = _useGetPrivilegeGroupsQuery({
        fetchPolicy: 'no-cache',
        variables: {},
    });
    return {
        error,
        loading,
        data: data?.getPrivilegeGroups,
        refetch,
    };
};
export const useCreateRoleMutation = () => {
    const [_createRole, { data: result, error, loading }] = useMutation(CreateRoleDocument);
    return {
        data: result?.Role_create,
        error,
        loading,
        createRole: (payload) => _createRole({
            variables: {
                payload,
            },
        }),
    };
};
export const useUpdateRoleMutation = () => {
    const [_updateRole, { data: result, error, loading }] = useMutation(UpdateRoleDocument);
    return {
        data: result?.Role_update,
        error,
        loading,
        updateRole: (payload) => _updateRole({
            variables: {
                payload,
            },
        }),
    };
};
export const onDownloadExportUtil = async (fetchUrl, onSuccess, onError) => {
    await fetch(fetchUrl)
        .then(async (res) => {
        if (!res?.ok) {
            const errResponse = await res.json();
            throw new Error(`Role export download failed due to ${res?.statusText}`);
        }
        const disposition = res?.headers?.get('Content-Disposition');
        const fileName = getFileName(disposition);
        return {
            blob: await res?.blob(),
            fileName,
        };
    })
        .then(file => {
        if (onSuccess) {
            onSuccess();
        }
        return downloadFileUtil(file);
    })
        .catch(error => {
        if (onError) {
            onError();
        }
    });
};
//# sourceMappingURL=role-service.js.map